import React, { useState, useRef } from "react";
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Paper,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import MathuraLogo from "../assets/logos/mathura.png";

const GST_RATES = [0, 5, 12, 18, 28];

export default function InvoiceForm() {
  // Supplier Details
  const supplierDetails = {
    name: "Mathura Group",
    addressline1: "No. 29/1, Pathivalar Colony, Madukkarai Road",
    addressline2: "Sundarapuram, Coimbatore - 641 024",
    gstin: "33ABCDE1234F2Z5",
    email: "support@mathuragroup.com",
    phone: "+91 99948 55443",
    alternatephone: " +91 63857 55443",
    bankName: "Test Bank",
    bankIFSC: "ABCD93939",
    bankAccount: "848488494984",
    logo: MathuraLogo,
  };

  // State
  const [recipientName, setRecipientName] = useState("");
  const [recipientAddress1, setRecipientAddress1] = useState("");
  const [recipientAddress2, setRecipientAddress2] = useState("");
  const [recipientContactDetails, SetRecipientContactDetails] = useState("");
  const [recipientGSTIN, setRecipientGSTIN] = useState("");
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [invoiceDate, setInvoiceDate] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [placeOfSupply, setPlaceOfSupply] = useState("");
  const [applyGST, setApplyGST] = useState(false);
  const [gstType, setGstType] = useState("");
  const [gstRate, setGstRate] = useState(0);
  const [discountChecked, setDiscountChecked] = useState(false);
  const [discountRate, setDiscountRate] = useState(0);
  const [roundOff, setRoundOff] = useState(false);
  const [paymentTerms, setPaymentTerms] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [companyGST, setCompanyGST] = useState("");
  const [bankName, setBankName] = useState("");
  const [branchName, setBranchName] = useState("");
  const [bankIFSC, setBankIFSC] = useState("");
  const [bankAccNo, setBankAccNo] = useState("");
  const [accName, setAccName] = useState("");
  const [deliveryAddName, setDeliveryAddName] = useState("");
  const [deliveryAddress1, setDeliveryAddress1] = useState("");
  const [deliveryAddress2, setDeliveryAddress2] = useState("");
  const [deliveryContactDetails, setDeliveryContactDetails] = useState("");
  const [deliveryGSTIN, setDeliveryGSTIN] = useState("");
  // Item lines
  const initialItem = { description: "", hsn: "", quantity: 1, unitPrice: 0 };
  const [items, setItems] = useState([{ ...initialItem }]);
  const addItem = () => setItems((prev) => [...prev, { ...initialItem }]);
  const updateItem = (idx, field, value) => {
    const arr = [...items];
    arr[idx][field] = value;
    setItems(arr);
  };

  // Totals
  const subtotal = items.reduce((sum, i) => sum + i.quantity * i.unitPrice, 0);
  const discountAmt = discountChecked ? (subtotal * discountRate) / 100 : 0;
  const taxableVal = subtotal - discountAmt;
  let cgstAmt = 0,
    sgstAmt = 0,
    igstAmt = 0;
  if (applyGST && gstRate) {
    if (gstType === "IGST") igstAmt = (taxableVal * gstRate) / 100;
    else {
      cgstAmt = (taxableVal * gstRate) / 200;
      sgstAmt = cgstAmt;
    }
  }
  const total = taxableVal + cgstAmt + sgstAmt + igstAmt;
  const finalTotal = roundOff ? Math.round(total) : total;
  const amountInWords = convertToWords(Math.round(finalTotal));

  // PDF ref
  const printRef = useRef();

  const handleSubmit = async (e) => {
    e.preventDefault();
    const canvas = await html2canvas(printRef.current);
    const img = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "pt", "a4");
    const w = pdf.internal.pageSize.getWidth();
    const h = (canvas.height * w) / canvas.width;
    pdf.addImage(img, "PNG", 0, 0, w, h);
    pdf.save(`Invoice_${invoiceNumber || "DOC"}.pdf`);
  };

  // Helper function to convert number to words (simplified)
  function convertToWords(num) {
    const single = [
      "",
      "One",
      "Two",
      "Three",
      "Four",
      "Five",
      "Six",
      "Seven",
      "Eight",
      "Nine",
    ];
    const teens = [
      "Ten",
      "Eleven",
      "Twelve",
      "Thirteen",
      "Fourteen",
      "Fifteen",
      "Sixteen",
      "Seventeen",
      "Eighteen",
      "Nineteen",
    ];
    const tens = [
      "",
      "",
      "Twenty",
      "Thirty",
      "Forty",
      "Fifty",
      "Sixty",
      "Seventy",
      "Eighty",
      "Ninety",
    ];

    if (num === 0) return "Zero";
    if (num < 10) return single[num];
    if (num < 20) return teens[num - 10];
    if (num < 100)
      return (
        tens[Math.floor(num / 10)] +
        (num % 10 !== 0 ? " " + single[num % 10] : "")
      );
    if (num < 1000)
      return (
        single[Math.floor(num / 100)] +
        " Hundred" +
        (num % 100 !== 0 ? " and " + convertToWords(num % 100) : "")
      );
    if (num < 100000)
      return (
        convertToWords(Math.floor(num / 1000)) +
        " Thousand" +
        (num % 1000 !== 0 ? " " + convertToWords(num % 1000) : "")
      );
    if (num < 10000000)
      return (
        convertToWords(Math.floor(num / 100000)) +
        " Lakh" +
        (num % 100000 !== 0 ? " " + convertToWords(num % 100000) : "")
      );
    return (
      convertToWords(Math.floor(num / 10000000)) +
      " Crore" +
      (num % 10000000 !== 0 ? " " + convertToWords(num % 10000000) : "")
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Enhanced Invoice Preview: A4 */}
      <Paper
        ref={printRef}
        elevation={0}
        sx={{
          width: "210mm",
          minHeight: "297mm",
          p: 3,
          border: "1px solid #ccc",
          m: "0 auto",
          boxSizing: "border-box",
          bgcolor: "#fff",
        }}
      >
        {/* Header Section */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 1,
            pb: 1,
            borderBottom: "2px solid #800001",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Box
              component="img"
              src={supplierDetails.logo}
              alt="Logo"
              sx={{ height: 80, mr: 2 }}
            />
            <Box>
              <Typography
                variant="h5"
                sx={{ fontWeight: "bold", color: "#800001" }}
              >
                {companyName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {supplierDetails.addressline1},<br />{" "}
                {supplierDetails.addressline2}
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>GSTIN:</strong> {companyGST}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ textAlign: "right", mt: 6 }}>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              <strong>Phone:</strong> {supplierDetails.phone}
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              <strong>Email:</strong> {supplierDetails.email}
            </Typography>
          </Box>
        </Box>

        {/* Invoice Title */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1,
          }}
        >
          <Typography
            variant="h4"
            align="center"
            sx={{
              fontWeight: "bold",
              color: "#800001",
            }}
          >
            TAX INVOICE
          </Typography>
          <Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                Invoice No:
              </Typography>
              <Typography variant="body2">{invoiceNumber}</Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                Invoice Date:
              </Typography>
              <Typography variant="body2">{invoiceDate}</Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="body2" sx={{ fontWeight: "bold", mr: 1 }}>
                Place of Service:{" "}
              </Typography>
              <Typography variant="body2">{placeOfSupply}</Typography>
            </Box>
          </Box>
        </Box>

        {/* Invoice Info & Recipient Section */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mb: 1,
          }}
        >
          {/* Bill To */}
          <Box sx={{ width: "48%" }}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: "#f8f9fa",
                border: "1px solid #e0e0e0",
                borderRadius: 1,
              }}
            >
              <Box sx={{ display: "flex", gap: 0.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: "400" }}>
                  Bill To:
                </Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                  {recipientName}
                </Typography>
              </Box>

              <Typography variant="body2" sx={{ mb: 0.5 }}>
                {recipientAddress1} <br />
                {recipientAddress2} <br />
                {recipientContactDetails}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: "bolder" }}>
                {recipientGSTIN}
              </Typography>
            </Paper>
          </Box>
          <Box sx={{ width: "48%" }}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: "#f8f9fa",
                border: "1px solid #e0e0e0",
                borderRadius: 1,
              }}
            >
              <Box sx={{ display: "flex", gap: 0.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: "400" }}>
                  Delivery To:
                </Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                  {deliveryAddName}
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                {deliveryAddress1} <br />
                {deliveryAddress2} <br />
                {deliveryContactDetails}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: "bolder" }}>
                {deliveryGSTIN}
              </Typography>
            </Paper>
          </Box>
        </Box>

        {/* Items Table */}
        <Box sx={{ mb: 1 }}>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: "bold",
              mb: 1,
              color: "#800001",
            }}
          >
            ITEM DETAILS
          </Typography>
          <Box
            sx={{
              borderRadius: 1,
              overflow: "hidden",
              border: "1px solid #e0e0e0",
            }}
          >
            {/* Header Row */}
            <Box
              sx={{
                display: "flex",
                bgcolor: "#f0f2f5",
                borderBottom: "1px solid #e0e0e0",
                p: 1.5,
              }}
            >
              <Box sx={{ flex: 3, fontWeight: "bold", fontSize: "0.875rem" }}>
                Description
              </Box>
              <Box sx={{ flex: 1, fontWeight: "bold", fontSize: "0.875rem" }}>
                HSN/SAC
              </Box>
              {/* <Box
                sx={{
                  width: 80,
                  textAlign: "right",
                  fontWeight: "bold",
                  fontSize: "0.875rem",
                }}
              >
                Qty
              </Box> */}
              <Box
                sx={{
                  width: 100,
                  textAlign: "right",
                  fontWeight: "bold",
                  fontSize: "0.875rem",
                }}
              >
                Unit Price
              </Box>
              <Box
                sx={{
                  width: 120,
                  textAlign: "right",
                  fontWeight: "bold",
                  fontSize: "0.875rem",
                }}
              >
                Amount
              </Box>
            </Box>

            {/* Item Rows */}
            {items.map((it, idx) => (
              <Box
                key={idx}
                sx={{
                  display: "flex",
                  p: 1.5,
                  borderBottom:
                    idx < items.length - 1 ? "1px solid #e0e0e0" : "none",
                  bgcolor: idx % 2 === 0 ? "#ffffff" : "#fafafa",
                }}
              >
                <Box sx={{ flex: 3, fontSize: "0.875rem" }}>
                  {it.description}
                </Box>
                <Box sx={{ flex: 1, fontSize: "0.875rem" }}>{it.hsn}</Box>
                {/* <Box
                  sx={{ width: 80, textAlign: "right", fontSize: "0.875rem" }}
                >
                  {it.quantity}
                </Box> */}
                <Box
                  sx={{ width: 100, textAlign: "right", fontSize: "0.875rem" }}
                >
                  ₹{it.unitPrice.toFixed(2)}
                </Box>
                <Box
                  sx={{ width: 120, textAlign: "right", fontSize: "0.875rem" }}
                >
                  ₹{(it.quantity * it.unitPrice).toFixed(2)}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Summary Section */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
          <Box
            sx={{
              width: "350px",
              border: "1px solid #e0e0e0",
              borderRadius: 1,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                p: 1.5,
                borderBottom: "1px solid #e0e0e0",
                bgcolor: "#f8f9fa",
              }}
            >
              <Typography variant="body2">Subtotal</Typography>
              <Typography variant="body2">₹{subtotal.toFixed(2)}</Typography>
            </Box>

            {discountChecked && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  p: 1.5,
                  borderBottom: "1px solid #e0e0e0",
                }}
              >
                <Typography variant="body2">
                  Discount ({discountRate}%)
                </Typography>
                <Typography variant="body2">
                  -₹{discountAmt.toFixed(2)}
                </Typography>
              </Box>
            )}

            {applyGST && (
              <>
                {gstType === "IGST" ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      p: 1.5,
                      borderBottom: "1px solid #e0e0e0",
                    }}
                  >
                    <Typography variant="body2">IGST ({gstRate}%)</Typography>
                    <Typography variant="body2">
                      ₹{igstAmt.toFixed(2)}
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        p: 1.5,
                        borderBottom: "1px solid #e0e0e0",
                      }}
                    >
                      <Typography variant="body2">
                        CGST ({gstRate / 2}%)
                      </Typography>
                      <Typography variant="body2">
                        ₹{cgstAmt.toFixed(2)}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        p: 1.5,
                        borderBottom: "1px solid #e0e0e0",
                      }}
                    >
                      <Typography variant="body2">
                        SGST ({gstRate / 2}%)
                      </Typography>
                      <Typography variant="body2">
                        ₹{sgstAmt.toFixed(2)}
                      </Typography>
                    </Box>
                  </>
                )}
              </>
            )}

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                p: 1.5,
                bgcolor: "#800001",
                color: "white",
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                TOTAL
              </Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                ₹{finalTotal.toFixed(2)}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Amount in Words */}
        <Box
          sx={{
            p: 2,
            bgcolor: "#f8f9fa",
            borderRadius: 1,
            mb: 1,
            border: "1px solid #e0e0e0",
          }}
        >
          <Typography variant="body2">
            <strong>Amount in Words:</strong> Rupees {amountInWords} Only
          </Typography>
        </Box>

        {/* Payment Information */}
        <Box sx={{ mb: 1 }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: "bold", mb: 1, color: "#800001" }}
          >
            PAYMENT DETAILS
          </Typography>
          <Box
            sx={{
              p: 1,
              border: "1px solid #e0e0e0",
              borderRadius: 1,
            }}
          >
            <Typography sx={{ mb: 0.5 }}>
              <strong>Bank Acc Name:</strong> {accName}
            </Typography>
            <Box
              sx={{ display: "flex", gap: 1, justifyContent: "space-between" }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                <Typography>
                  <strong>Bank Name:</strong> {bankName}
                </Typography>
                <Typography>
                  <strong>Acc No:</strong> {bankAccNo}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                <Typography>
                  <strong>Branch:</strong> {branchName}
                </Typography>
                <Typography>
                  <strong>IFSC:</strong> {bankIFSC}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Footer */}
        <Box
          sx={{
            // mt: "auto",
            pt: 0.5,
            borderTop: "1px solid #e0e0e0",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Box>
            <Typography variant="body2" color="text.secondary">
              Thank you for your business!
            </Typography>
          </Box>
          <Box sx={{ textAlign: "right" }}>
            <Typography variant="body2" sx={{ fontWeight: "bold" }}>
              {companyName}
            </Typography>
            <Box
              sx={{
                height: 60,
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-end",
              }}
            >
              <Typography variant="body2">Authorized Signatory</Typography>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Form Inputs */}
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{ mt: 4, display: "flex", flexDirection: "column", gap: 2 }}
      >
        <Typography variant="h6">Enter Details</Typography>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Box
            sx={{ display: "flex", gap: 2, flexDirection: "column", flex: 1 }}
          >
            <TextField
              label="Company Name"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Company GST"
              value={companyGST}
              onChange={(e) => setCompanyGST(e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Invoice No"
              value={invoiceNumber}
              onChange={(e) => setInvoiceNumber(e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Invoice Date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={invoiceDate}
              onChange={(e) => setInvoiceDate(e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Place of Service"
              value={placeOfSupply}
              onChange={(e) => setPlaceOfSupply(e.target.value)}
              fullWidth
              required
            />
          </Box>
          <Box
            sx={{ display: "flex", gap: 2, flexDirection: "column", flex: 1 }}
          >
            <TextField
              label="Bank Account Name"
              value={accName}
              onChange={(e) => setAccName(e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Bank Name"
              value={bankName}
              onChange={(e) => setBankName(e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Bank Acc No"
              value={bankAccNo}
              onChange={(e) => setBankAccNo(e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Bank Branch"
              value={branchName}
              onChange={(e) => setBranchName(e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Bank IFSC"
              value={bankIFSC}
              onChange={(e) => setBankIFSC(e.target.value)}
              fullWidth
              required
            />
          </Box>
        </Box>
        <Typography
          sx={{ textAlign: "center", fontSize: "20px", fontWeight: "bolder" }}
        >
          Address
        </Typography>
        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
          <Box
            sx={{ display: "flex", gap: 2, flexDirection: "column", flex: 1 }}
          >
            <TextField
              label="Recipient Name"
              value={recipientName}
              onChange={(e) => setRecipientName(e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Recipient GSTIN"
              value={recipientGSTIN}
              onChange={(e) => setRecipientGSTIN(e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Recipient Contact"
              value={recipientContactDetails}
              onChange={(e) => SetRecipientContactDetails(e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Recipient Address 1"
              value={recipientAddress1}
              onChange={(e) => setRecipientAddress1(e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Recipient Address 2"
              value={recipientAddress2}
              onChange={(e) => setRecipientAddress2(e.target.value)}
              fullWidth
              required
            />
          </Box>
          <Box
            sx={{ display: "flex", gap: 2, flexDirection: "column", flex: 1 }}
          >
            <TextField
              label="Delivery Name"
              value={deliveryAddName}
              onChange={(e) => setDeliveryAddName(e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Delivery GSTIN"
              value={deliveryGSTIN}
              onChange={(e) => setDeliveryGSTIN(e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Delivery Contact"
              value={deliveryContactDetails}
              onChange={(e) => setDeliveryContactDetails(e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Delivery Address 1"
              value={deliveryAddress1}
              onChange={(e) => setDeliveryAddress1(e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Delivery Address 2"
              value={deliveryAddress2}
              onChange={(e) => setDeliveryAddress2(e.target.value)}
              fullWidth
              required
            />
          </Box>
        </Box>

        {/* <TextField
          label="Payment Terms"
          value={paymentTerms}
          onChange={(e) => setPaymentTerms(e.target.value)}
          fullWidth
          placeholder="e.g., Net 30 days"
        /> */}
        <Typography variant="subtitle1">Items</Typography>
        {items.map((item, idx) => (
          <Box
            key={idx}
            sx={{
              display: "flex",
              gap: 2,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <TextField
              label="Description"
              value={item.description}
              onChange={(e) => updateItem(idx, "description", e.target.value)}
              sx={{ flex: 2 }}
              required
            />
            <TextField
              label="HSN/SAC"
              value={item.hsn}
              onChange={(e) => updateItem(idx, "hsn", e.target.value)}
              sx={{ flex: 1 }}
              required
            />
            {/* <TextField
              label="Qty"
              type="number"
              value={item.quantity}
              onChange={(e) =>
                updateItem(idx, "quantity", Number(e.target.value))
              }
              sx={{ width: 80 }}
              required
            /> */}
            <TextField
              label="Unit Price"
              type="number"
              value={item.unitPrice}
              onChange={(e) =>
                updateItem(idx, "unitPrice", Number(e.target.value))
              }
              sx={{ width: 120 }}
              required
            />
          </Box>
        ))}
        <Button
          startIcon={<AddCircleOutlineIcon />}
          onClick={addItem}
          sx={{ alignSelf: "flex-start" }}
        >
          Add Item
        </Button>
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={applyGST}
                onChange={(e) => setApplyGST(e.target.checked)}
              />
            }
            label="Apply GST"
          />
          {applyGST && (
            <>
              <FormControl sx={{ minWidth: 120 }}>
                <InputLabel>GST Type</InputLabel>
                <Select
                  value={gstType}
                  onChange={(e) => setGstType(e.target.value)}
                  label="GST Type"
                  required
                >
                  <MenuItem value="IGST">IGST</MenuItem>
                  <MenuItem value="CGST_SGST">CGST + SGST</MenuItem>
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 100 }}>
                <InputLabel>Rate (%)</InputLabel>
                <Select
                  value={gstRate}
                  onChange={(e) => setGstRate(Number(e.target.value))}
                  label="Rate"
                  required
                >
                  {GST_RATES.map((r) => (
                    <MenuItem key={r} value={r}>
                      {r}%
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          )}
          <FormControlLabel
            control={
              <Checkbox
                checked={discountChecked}
                onChange={(e) => setDiscountChecked(e.target.checked)}
              />
            }
            label="Apply Discount"
          />
          {discountChecked && (
            <TextField
              label="Discount %"
              type="number"
              value={discountRate}
              onChange={(e) => setDiscountRate(Number(e.target.value))}
              sx={{ width: 120 }}
              required
            />
          )}
          <FormControlLabel
            control={
              <Checkbox
                checked={roundOff}
                onChange={(e) => setRoundOff(e.target.checked)}
              />
            }
            label="Round Off Total"
          />
        </Box>

        <Button variant="contained" type="submit" sx={{ mt: 2 }}>
          Download Invoice
        </Button>
      </Box>
    </Container>
  );
}
